import type { Static } from 'runtypes';
import * as rt from 'runtypes';
import { AuditableRunType } from './controlFields';

const { Boolean, InstanceOf, Literal, Null, Optional, Record, String, Union, Array, Number } = rt;

enum LoginProviderEnum {
  Local = 'Local',
  ActiveDirectory = 'ActiveDirectory',
  OIDC = 'OIDC',
  Unknown = 'Unknown', // this is a hack for when we don't know the login provider
  BearerToken = 'BearerToken',
  BcApiInternal = 'BcApiInternal', // bc-api internal auth - trusts JWT signature, no password check
  JWT = 'JWT',
  Test = 'Test',
  EmbedToken = 'EmbedToken', // support auth for iframe embeds
  OptionalEmbedToken = 'OptionalEmbedToken', // case where we can use an embed token or something else
  SignUp = 'SignUp', // internal passport strategy for account registration flow
  LinkAccounts = 'LinkAccounts', // internal passport strategy for linking a local account to a third-party identity
}

type ThirdPartyAuthenticationUserDetails = {
  username: string;
  email: string;
  name: string;
  firstName: string;
  lastName: string;
};

type DiscoveredAccountDetails = {
  avatarColor?: string | null;
  rank?: string | null;
  jobTitle?: string | null;
  organization?: string | null;
  parentOrganization?: string | null;
};

type ThirdPartyAuthenticationProfileType = {
  userDetails: ThirdPartyAuthenticationUserDetails;
  mustRegister: boolean;
  hasUnlinkedProfile: boolean;
  loginProvider: LoginProviderEnum;
  providedId: string;
  idToken?: string;
  resourceAccess?: { [key: string]: any };
};

enum AccountLinkingFailuresEnum {
  INVALID_PASSWORD = 'Invalid user name or password.',
  ALREADY_LINKED = 'This account is already linked to an identity.',
  NO_THIRD_PARTY_PROFILE = 'No third-party authentication profile found in session.',
  DEACTIVATED_ACCOUNT = 'Deleted account cannot be linked to SSO identity.',
}

type UserPersistentStateUpdateType = {
  key: string;
  value: string | number | boolean | Date | object;
};

type UserPersistentStateReadType = UserPersistentStateUpdateType & {
  createdAt?: Date;
  updatedAt?: Date;
};

const EMAIL_VERIFICATION_RESULTS = {
  success: 'success',
  failure: 'failure',
  tokenExpired: 'tokenExpired',
  invalid: 'invalid',
  sent: 'sent',
  resent: 'resent',
  alreadyVerified: 'alreadyVerified',
} as const;

type EmailVerificationResult = keyof typeof EMAIL_VERIFICATION_RESULTS;

const AccountRunType = Record({
  name: String,
  avatarUrl: Optional(Union(String, Null)),
  email: Optional(String),
  emailVerified: Optional(Boolean),
  authorized: Optional(Boolean),
  hasPassword: Optional(Boolean),
  hasLinkedIdentity: Optional(Boolean),
  firstName: Optional(Union(String, Null)),
  lastName: Optional(Union(String, Null)),
  language: Optional(Union(String, Null)),
  rank: Optional(Union(String, Null)),
  jobTitle: Optional(Union(String, Null)),
  avatarColor: Optional(Union(String, Null)),
  organization: Optional(Union(String, Null)), // Unit
  parentOrganization: Optional(Union(String, Null)), // Parent Unit
  resetHashExpiration: Optional(Union(InstanceOf(Date), Null)),
  requirePasswordChange: Optional(Union(Boolean, Null)),
  lastSignIn: Optional(Union(InstanceOf(Date), Null)),
  showUnclassifiedDisclaimer: Optional(Union(Boolean, Null)),
  emailVerificationEnabled: Optional(Union(Boolean, Null)),
  emailVerificationRequired: Optional(Union(Boolean, Null)),
  userVerificationEnabled: Optional(Union(Boolean, Null)),
  accountType: Optional(Union(Literal('user'), Literal('api'))),
  accountVerifiedStatus: Optional(Union(Boolean, Null)),
  accountVerifiedOn: Optional(Union(InstanceOf(Date), Null)),
  accountVerifiedBy: Optional(Union(String, Null)),
  accountVerifiedByUuid: Optional(Union(String, Null)),
  accountVerifiedByName: Optional(Union(String, Null)),
  username: Optional(String),
  favoritePlans: Optional(InstanceOf(Set<number>)),
  isLocked: Optional(Boolean),
});
type AccountType = Static<typeof AccountRunType>;

const AccountUpdatedRunType = AccountRunType.And(
  Record({
    id: Number,
    uuid: Optional(Union(String, Null)),
    resetHash: Optional(Union(String, Null)),
  })
);
type AccountUpdatedType = Static<typeof AccountUpdatedRunType>;

const AccountSavedRunType = AccountRunType.And(AuditableRunType).And(
  Record({ id: Number, uuid: Optional(Union(String, Null)), deleted: Boolean })
);
type AccountSavedType = Static<typeof AccountSavedRunType>;

const APIAccountRunType = Record({
  name: String,
  organizationId: Number,
  description: String,
  pocEmail: String,
  poc2Email: Optional(String),
  id: Optional(Number),
  uuid: Optional(String),
  domain: Optional(String),
  accountType: Optional(Literal('api')),
});
type APIAccountType = Static<typeof APIAccountRunType>;

const APIAccountUpdatedRunType = APIAccountRunType.And(
  Record({ id: Number, uuid: Optional(String) })
);

const APIAccountSavedRunType = APIAccountUpdatedRunType.And(AuditableRunType).And(
  Record({ deleted: Boolean })
);
type APIAccountSavedType = Static<typeof APIAccountSavedRunType>;

const IdentityRunType = Record({
  accountId: Number,
  accountUuid: Optional(Union(String, Null)),
  displayName: Union(String, Null),
  emails: Union(Array(String), Null),
  provider: String,
  providedId: String,
  username: Optional(Union(String, Null)),
});
type IdentityType = Static<typeof IdentityRunType>;

const IdentitySavedRunType = IdentityRunType.And(AuditableRunType);
type IdentitySavedType = Static<typeof IdentitySavedRunType>;
type MappedIdentityRow = Pick<
  IdentitySavedType,
  'provider' | 'providedId' | 'accountId' | 'accountUuid' | 'createdAt' | 'updatedAt'
>;

type UserLoginData = {
  /**
   * Local Onebrief account associated with the user, if any. Nullable if the user has not previously registered
   */
  account?: Nullable<AccountSavedType>;
  activeDirectoryProfile?: ThirdPartyAuthenticationProfileType;
  oidcProfile?: ThirdPartyAuthenticationProfileType;
  jwtProfile?: ThirdPartyAuthenticationProfileType;
  /**
   * Display fields for a local account matched via email during an SSO login attempt.
   * Separate from `userDetails` on the profile types, which only holds IdP-provided data.
   */
  discoveredAccountDetails?: DiscoveredAccountDetails;
  /**
   * Auth strategy that was used for this login attempt; will be one of the providers defined in `LoginProviderEnum`
   */
  type: LoginProviderEnum;
};

// Current actor identity; use on audit write paths and dual-path SQL; not for target accounts (use AsAccount)
type AsUserType = {
  id: number;
  uuid?: string | null;
};

// Target account identity for dual-path SQL; at least one of id or uuid must be present; not for the actor (use AsUserType)
type AsAccount = { id: number; uuid?: string | null } | { id?: number | null; uuid: string };

// Coerced account params safe for SQL; id and uuid both normalized as nullable
type SqlAccountParams = { id: number | null; uuid: string | null };

// Foreign-key reference to an account
type AccountRef = {
  accountId?: number | null;
  accountUuid?: string | null;
};

// Audit creator reference
type AuditCreatorRef = {
  createdBy?: number | null;
  createdByUuid?: string | null;
};

// This type reflects `passport`s handling of ActiveDirectory
type Profile = {
  name: {
    givenName: string;
    familyName: string;
  };
  displayName: string;
  id: string;
  fullName?: string;
  emails?: Array<{
    value: string;
    primary: boolean;
  }>;
  provider: string;
  username?: string;
};

export {
  AccountLinkingFailuresEnum,
  AccountRunType,
  AccountSavedRunType,
  AccountUpdatedRunType,
  APIAccountRunType,
  APIAccountSavedRunType,
  APIAccountUpdatedRunType,
  EMAIL_VERIFICATION_RESULTS,
  EmailVerificationResult,
  IdentityRunType,
  IdentitySavedRunType,
  LoginProviderEnum,
};

export type {
  AccountRef,
  AccountSavedType,
  AccountType,
  AccountUpdatedType,
  APIAccountSavedType,
  APIAccountType,
  AsAccount,
  AsUserType,
  AuditCreatorRef,
  IdentitySavedType,
  IdentityType,
  MappedIdentityRow,
  Profile,
  SqlAccountParams,
  DiscoveredAccountDetails,
  ThirdPartyAuthenticationProfileType,
  UserLoginData,
  UserPersistentStateReadType,
  UserPersistentStateUpdateType,
  ThirdPartyAuthenticationUserDetails,
};
